import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Search, Plus, Shield, AlertTriangle, BarChart3, Moon, Sun, Filter, Clock, TrendingUp, Users, Pill, DollarSign, Scissors, User, XCircle, Eye, Activity } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { toast, Toaster } from "sonner";

// Utiliser la configuration d'environnement depuis .env
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debug info - will help identify the issue between live and preview
console.log("🌍 Environment Debug Info:");
console.log("📍 BACKEND_URL:", BACKEND_URL);
console.log("🔗 API URL:", API);
console.log("🌐 Window Location:", window.location.href);

function App() {
  // Authentication state - Remember for 1 week
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if user is already authenticated and session is still valid
    const authData = localStorage.getItem('checkClientAuth');
    
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const now = new Date().getTime();
        const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        
        // Check if authentication is still valid (less than 1 week old)
        if (now - parsed.timestamp < oneWeek) {
          return true;
        } else {
          // Remove expired authentication
          localStorage.removeItem('checkClientAuth');
          return false;
        }
      } catch (error) {
        localStorage.removeItem('checkClientAuth');
        return false;
      }
    }
    return false;
  });
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  
  // Admin state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminReports, setAdminReports] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [adminSearchTerm, setAdminSearchTerm] = useState("");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [liveResults, setLiveResults] = useState([]);
  const [showLiveResults, setShowLiveResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [activeTab, setActiveTab] = useState("search");
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [showFilteredResults, setShowFilteredResults] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [analytics, setAnalytics] = useState(null);
  const [filterBy, setFilterBy] = useState("all");
  
  // Add report form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReport, setNewReport] = useState({
    phone_number: "",
    status: "",
    city: "",
    comment: "",
    username: ""
  });
  
  // Analytics popup state
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [selectedPhoneData, setSelectedPhoneData] = useState(null);

  // Password protection
  const handleLogin = () => {
    if (password === "BMG25") {
      const authData = {
        authenticated: true,
        timestamp: new Date().getTime()
      };
      
      // Store authentication in localStorage for 1 week
      localStorage.setItem('checkClientAuth', JSON.stringify(authData));
      
      setIsAuthenticated(true);
      setAuthError("");
      toast.success("Access granted (valid for 1 week)");
    } else {
      setAuthError("Incorrect password");
      toast.error("Access denied");
    }
  };

  const handleLogout = () => {
    // Remove authentication from localStorage
    localStorage.removeItem('checkClientAuth');
    
    setIsAuthenticated(false);
    setPassword("");
    setAuthError("");
    toast.info("Logged out");
  };

  // Helper function to get remaining authentication time
  const getAuthTimeRemaining = () => {
    const authData = localStorage.getItem('checkClientAuth');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const now = new Date().getTime();
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        const timeLeft = oneWeek - (now - parsed.timestamp);
        
        if (timeLeft > 0) {
          const daysLeft = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
          const hoursLeft = Math.floor((timeLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
          
          if (daysLeft > 0) {
            return `${daysLeft} day${daysLeft > 1 ? 's' : ''} left`;
          } else if (hoursLeft > 0) {
            return `${hoursLeft} hour${hoursLeft > 1 ? 's' : ''} left`;
          } else {
            return 'Expires soon';
          }
        }
      } catch (error) {
        return '';
      }
    }
    return '';
  };

  // Helper function to format city names (uppercase)
  const formatCity = (city) => {
    const cityMap = {
      'Geneva': 'GENÈVE',
      'Zurich': 'ZURICH', 
      'Lausanne': 'LAUSANNE',
      'Other': 'AUTRE'
    };
    
    return cityMap[city] || city?.toUpperCase() || '';
  };

  // Helper function to format city and status display
  const formatCityAndStatus = (city, status) => {
    const statusInfo = getStatusInfo(status);
    const cityName = formatCity(city);
    return `${cityName} - ${statusInfo.label}`;
  };
  
  // Helper function to get provider from phone number
  const getProviderFromPhone = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Clean the phone number - remove all spaces, formatting, and + sign
    let cleanPhone = phoneNumber.toString().replace(/\s/g, '').replace(/\*/g, '').replace(/\+/g, '');
    
    // Switzerland (+41)
    if (cleanPhone.startsWith('41')) {
      const prefix = cleanPhone.substring(2, 5); // Get 3 digits after 41
      if (prefix.startsWith('79')) return 'Swisscom';
      if (prefix.startsWith('78')) return 'Salt';  
      if (prefix.startsWith('77')) return 'Sunrise';
      if (prefix.startsWith('76')) return 'MVNO';
    }
    
    // France (+33)
    if (cleanPhone.startsWith('33')) {
      const prefix = cleanPhone.substring(2, 4); // Get 2 digits after 33
      if (prefix === '60' || prefix === '61' || prefix === '62' || prefix === '63') return 'Orange';
      if (prefix === '64' || prefix === '65' || prefix === '66' || prefix === '67') return 'SFR';
      if (prefix === '68' || prefix === '69') return 'Bouygues';
      if (prefix.startsWith('6')) return 'Mobile FR';
      if (prefix.startsWith('7')) return 'Mobile FR';
    }
    
    // Germany (+49)
    if (cleanPhone.startsWith('49')) {
      const prefix = cleanPhone.substring(2, 5); // Get 3 digits after 49
      if (prefix.startsWith('150') || prefix.startsWith('160') || prefix.startsWith('170') || prefix.startsWith('171') || prefix.startsWith('175')) return 'T-Mobile';
      if (prefix.startsWith('151') || prefix.startsWith('157') || prefix.startsWith('159') || prefix.startsWith('163') || prefix.startsWith('177') || prefix.startsWith('178')) return 'Vodafone';
      if (prefix.startsWith('152') || prefix.startsWith('162') || prefix.startsWith('172') || prefix.startsWith('173') || prefix.startsWith('174')) return 'O2';
      if (prefix.startsWith('15') || prefix.startsWith('16') || prefix.startsWith('17')) return 'Mobile DE';
    }
    
    // Italy (+39)
    if (cleanPhone.startsWith('39')) {
      const prefix = cleanPhone.substring(2, 5);
      if (prefix.startsWith('33') || prefix.startsWith('36') || prefix.startsWith('38')) return 'TIM';
      if (prefix.startsWith('34') || prefix.startsWith('37')) return 'Vodafone';
      if (prefix.startsWith('32') || prefix.startsWith('35')) return 'WindTre';
      if (prefix.startsWith('3')) return 'Mobile IT';
    }
    
    // USA/Canada (+1) 
    if (cleanPhone.startsWith('1')) {
      return 'North America';
    }
    
    // UK (+44)
    if (cleanPhone.startsWith('44')) {
      const prefix = cleanPhone.substring(2, 4);
      if (prefix === '74' || prefix === '75' || prefix === '76' || prefix === '77' || prefix === '78' || prefix === '79') return 'Mobile UK';
      return 'UK';
    }
    
    return ''; // Return empty if provider cannot be determined
  };

  // Helper function to get country from phone number
  const getCountryFromPhone = (phoneNumber) => {
    const countryMap = {
      '41': '🇨🇭 Switzerland',
      '33': '🇫🇷 France', 
      '49': '🇩🇪 Germany',
      '39': '🇮🇹 Italy',
      '43': '🇦🇹 Austria',
      '1': '🇺🇸 USA/Canada',
      '44': '🇬🇧 UK',
      '34': '🇪🇸 Spain',
      '31': '🇳🇱 Netherlands',
      '32': '🇧🇪 Belgium',
      '46': '🇸🇪 Sweden',
      '47': '🇳🇴 Norway',
      '45': '🇩🇰 Denmark',
      '358': '🇫🇮 Finland',
      '420': '🇨🇿 Czech Republic',
      '48': '🇵🇱 Poland',
      '971': '🇦🇪 UAE',
      '966': '🇸🇦 Saudi Arabia',
      '212': '🇲🇦 Morocco',
      '216': '🇹🇳 Tunisia',
      '213': '🇩🇿 Algeria',
      '20': '🇪🇬 Egypt',
      '218': '🇱🇾 Libya',
      '249': '🇸🇩 Sudan',
      '251': '🇪🇹 Ethiopia',
      '254': '🇰🇪 Kenya',
      '27': '🇿🇦 South Africa',
      '234': '🇳🇬 Nigeria',
      '233': '🇬🇭 Ghana',
      '225': '🇨🇮 Ivory Coast',
      '221': '🇸🇳 Senegal',
      '91': '🇮🇳 India',
      '92': '🇵🇰 Pakistan',
      '880': '🇧🇩 Bangladesh',
      '94': '🇱🇰 Sri Lanka',
      '95': '🇲🇲 Myanmar',
      '86': '🇨🇳 China',
      '852': '🇭🇰 Hong Kong',
      '853': '🇲🇴 Macao',
      '886': '🇹🇼 Taiwan',
      '81': '🇯🇵 Japan',
      '82': '🇰🇷 South Korea',
      '65': '🇸🇬 Singapore',
      '60': '🇲🇾 Malaysia',
      '66': '🇹🇭 Thailand',
      '84': '🇻🇳 Vietnam',
      '62': '🇮🇩 Indonesia',
      '63': '🇵🇭 Philippines',
      '61': '🇦🇺 Australia',
      '64': '🇳🇿 New Zealand',
      '55': '🇧🇷 Brazil',
      '52': '🇲🇽 Mexico',
      '54': '🇦🇷 Argentina',
      '56': '🇨🇱 Chile',
      '57': '🇨🇴 Colombia',
      '58': '🇻🇪 Venezuela',
      '51': '🇵🇪 Peru',
      '593': '🇪🇨 Ecuador',
      '598': '🇺🇾 Uruguay',
      '595': '🇵🇾 Paraguay',
      '591': '🇧🇴 Bolivia',
      '90': '🇹🇷 Turkey',
      '98': '🇮🇷 Iran',
      '964': '🇮🇶 Iraq',
      '962': '🇯🇴 Jordan',
      '961': '🇱🇧 Lebanon',
      '963': '🇸🇾 Syria',
      '972': '🇮🇱 Israel',
      '970': '🇵🇸 Palestine',
      '965': '🇰🇼 Kuwait',
      '974': '🇶🇦 Qatar',
      '973': '🇧🇭 Bahrain',
      '968': '🇴🇲 Oman',
      '967': '🇾🇪 Yemen',
      '30': '🇬🇷 Greece',
      '351': '🇵🇹 Portugal',
      '353': '🇮🇪 Ireland',
      '380': '🇺🇦 Ukraine',
      '375': '🇧🇾 Belarus',
      '374': '🇦🇲 Armenia',
      '995': '🇬🇪 Georgia',
      '994': '🇦🇿 Azerbaijan',
      '7': '🇷🇺 Russia/Kazakhstan',
      '359': '🇧🇬 Bulgaria',
      '40': '🇷🇴 Romania',
      '36': '🇭🇺 Hungary',
      '385': '🇭🇷 Croatia',
      '381': '🇷🇸 Serbia',
      '386': '🇸🇮 Slovenia',
      '370': '🇱🇹 Lithuania',
      '371': '🇱🇻 Latvia',
      '372': '🇪🇪 Estonia',
      '373': '🇲🇩 Moldova',
      '389': '🇲🇰 North Macedonia',
      '382': '🇲🇪 Montenegro',
      '387': '🇧🇦 Bosnia',
      '383': '🇽🇰 Kosovo',
      '378': '🇸🇲 San Marino',
      '376': '🇦🇩 Andorra',
      '377': '🇲🇨 Monaco',
      '379': '🇻🇦 Vatican',
      '355': '🇦🇱 Albania'
    };

    if (!phoneNumber) return '🌍 Unknown';
    
    // Clean the phone number - remove all spaces, formatting, and + sign
    let cleanPhone = phoneNumber.toString().replace(/\s/g, '').replace(/\*/g, '').replace(/\+/g, '');
    
    // Try different prefix lengths (1-4 digits) starting from the longest
    for (let len = 3; len >= 1; len--) {
      if (cleanPhone.length < len) continue;
      
      const prefix = cleanPhone.substring(0, len);
      if (countryMap[prefix]) {
        return countryMap[prefix];
      }
    }
    
    return '🌍 Other';
  };

  // Helper function to get country and provider combined
  const getCountryAndProvider = (phoneNumber) => {
    const country = getCountryFromPhone(phoneNumber);
    const provider = getProviderFromPhone(phoneNumber);
    
    if (provider) {
      return `${country} • ${provider}`;
    }
    return country;
  };

  // Helper function to create Last Reported Numbers from adminReports
  const getLastReportedNumbers = () => {
    if (!adminReports || adminReports.length === 0) return [];
    
    const phoneLatest = {};
    
    // Group by phone number, keeping only the latest report for each
    adminReports.forEach(report => {
      const phone = report.phone_number;
      if (!phoneLatest[phone]) {
        phoneLatest[phone] = {
          phone_number: phone,
          latest_report: report.created_at,
          status: report.status,
          city: report.city || 'Other',
          comment: report.comment || ''
        };
      }
    });
    
    // Convert to array and take first 20 (already sorted by date desc from API)
    return Object.values(phoneLatest).slice(0, 20);
  };

  // Helper function to format phone number with spaces and mask last 2 digits
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    let phone = phoneNumber.toString().replace(/\s/g, ''); // Remove existing spaces
    
    // If it doesn't start with +, add it
    if (!phone.startsWith('+')) {
      phone = '+' + phone;
    }
    
    // Remove the + for processing, we'll add it back
    let digits = phone.substring(1);
    
    // Mask the last 2 digits with stars
    if (digits.length >= 2) {
      digits = digits.substring(0, digits.length - 2) + '**';
    }
    
    // Add spaces for Swiss format: +41 79 810 69 **
    if (phone.startsWith('+41')) {
      // Swiss format: +41 XX XXX XX XX
      if (digits.length >= 9) {
        return `+41 ${digits.substring(2, 4)} ${digits.substring(4, 7)} ${digits.substring(7, 9)} ${digits.substring(9)}`;
      }
    }
    
    // French format: +33 X XX XX XX XX
    if (phone.startsWith('+33')) {
      if (digits.length >= 10) {
        return `+33 ${digits.substring(2, 3)} ${digits.substring(3, 5)} ${digits.substring(5, 7)} ${digits.substring(7, 9)} ${digits.substring(9)}`;
      }
    }
    
    // German format: +49 XXX XXXXXXX
    if (phone.startsWith('+49')) {
      if (digits.length >= 10) {
        return `+49 ${digits.substring(2, 5)} ${digits.substring(5, 8)} ${digits.substring(8, 10)} ${digits.substring(10)}`;
      }
    }
    
    // Generic international format: +XXX XX XXX XX XX
    if (digits.length >= 8) {
      // Find country code length
      let countryCodeLength = 1;
      if (phone.startsWith('+1') || phone.startsWith('+7')) countryCodeLength = 1;
      else if (phone.startsWith('+33') || phone.startsWith('+41') || phone.startsWith('+49') || phone.startsWith('+44')) countryCodeLength = 2;
      else if (phone.startsWith('+358') || phone.startsWith('+420') || phone.startsWith('+971')) countryCodeLength = 3;
      
      const countryCode = digits.substring(0, countryCodeLength);
      const rest = digits.substring(countryCodeLength);
      
      // Format the rest with spaces every 2-3 digits
      let formatted = `+${countryCode}`;
      for (let i = 0; i < rest.length; i += 2) {
        if (i > 0) formatted += ' ';
        formatted += rest.substring(i, i + 2);
      }
      return formatted;
    }
    
    // Fallback: just add the + back and mask
    return '+' + digits;
  };
  // Helper function to get status display info with new colors
  const getStatusInfo = (status) => {
    const statusMap = {
      'safe': {
        label: 'Safe Numbers',
        icon: Shield,
        className: 'bg-[#62d7d1] text-black'
      },
      'drugs_abusers': {
        label: 'Drugs Abuser',
        icon: AlertTriangle,
        className: 'bg-[#f7546b] text-white'
      },
      'health_risk_clients': {
        label: 'Health Risk',
        icon: AlertTriangle,
        className: 'bg-[#f9663d] text-white'
      },
      'time_wasters': {
        label: 'Time Waster',
        icon: Clock,
        className: 'bg-[#ffc14d] text-black'
      },
      'non_payment_clients': {
        label: 'Non-Payment',
        icon: AlertTriangle,
        className: 'bg-[#ffbb8a] text-black'
      },
      'aggressive_clients': {
        label: 'Aggressive Client',
        icon: AlertTriangle,
        className: 'bg-[#f7546b] text-white'
      },
      'no_show_clients': {
        label: 'No-Show Client',
        icon: Clock,
        className: 'bg-[#ffd8e9] text-black'
      }
    };
    
    return statusMap[status] || statusMap['time_wasters'];
  };
  // Auto-refresh data every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === "reports") {
        fetchReports();
      } else if (activeTab === "analytics") {
        fetchAnalytics();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [activeTab]);

  // Handle clicking outside live search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLiveResults && !event.target.closest('.relative')) {
        setShowLiveResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLiveResults]);

  // Initial data load
  useEffect(() => {
    if (activeTab === "reports") {
      fetchReports();
    } else if (activeTab === "analytics") {
      fetchAnalytics();
    }
  }, [activeTab]);

  // Theme toggle
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Effect to fetch admin data when panel opens
  useEffect(() => {
    if (showAdminPanel) {
      fetchAllReports();
      fetchAdminStats();
    }
  }, [showAdminPanel]);

  // Live search function
  const performLiveSearch = async (query) => {
    if (query.length < 3) {
      setLiveResults([]);
      setShowLiveResults(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/live-search`, { params: { q: query } });
      setLiveResults(response.data.results || []);
      setShowLiveResults(true);
    } catch (error) {
      console.error("Live search error:", error);
      setLiveResults([]);
    }
  };

  // Handle search input change with live search
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous results when typing
    if (searchResult) {
      setSearchResult(null);
    }
    
    // Perform live search with debouncing
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      performLiveSearch(value);
    }, 300); // 300ms debounce
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    setLoading(true);
    setShowLiveResults(false);
    
    try {
      const response = await axios.get(`${API}/search/${encodeURIComponent(searchQuery)}`);
      setSearchResult(response.data);
      
      if (response.data.found) {
        if (response.data.multiple_results) {
          toast.success(`Found ${response.data.total_matches} matching numbers`);
        } else {
          toast.success("Phone number found in database");
        }
      } else {
        toast.info("No reports found for this number");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Error searching phone number");
      setSearchResult({
        phone_number: searchQuery,
        found: false,
        message: "Error occurred during search"
      });
    } finally {
      setLoading(false);
    }
  };

  // Select a result from live search
  const selectLiveResult = (result) => {
    setSearchQuery(result.phone_number);
    setShowLiveResults(false);
    handleSearchForNumber(result.phone_number);
  };

  // Search for specific number
  const handleSearchForNumber = async (phoneNumber) => {
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/search/${encodeURIComponent(phoneNumber)}`);
      
      if (response.data.found) {
        // Open popup with phone details instead of changing tabs
        setSelectedPhoneData(response.data);
        setShowPhonePopup(true);
        toast.success("Phone number details loaded");
      } else {
        toast.error("Phone number not found");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Error loading phone number details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async (filter = null) => {
    try {
      const url = filter ? `${API}/reports?filter_by=${filter}&limit=2000` : `${API}/reports?limit=2000`;
      const response = await axios.get(url);
      
      // Group reports by phone number to show all comments together
      const groupedReports = {};
      response.data.forEach(report => {
        const phoneNumber = report.phone_number;
        if (!groupedReports[phoneNumber]) {
          groupedReports[phoneNumber] = {
            phone_number: phoneNumber,
            reports: [],
            all_comments: [],
            latest_date: report.created_at,
            primary_status: report.status,
            city: report.city
          };
        }
        
        groupedReports[phoneNumber].reports.push(report);
        if (report.comment) {
          groupedReports[phoneNumber].all_comments.push(report.comment);
        }
        
        // Update latest date
        if (new Date(report.created_at) > new Date(groupedReports[phoneNumber].latest_date)) {
          groupedReports[phoneNumber].latest_date = report.created_at;
        }
        
        // Determine primary status (highest priority)
        const statusPriority = {
          "drugs_abusers": 4,
          "health_risk_clients": 3,
          "non_payment_clients": 2,
          "time_wasters": 1,
          "safe": 0
        };
        
        const currentPriority = statusPriority[groupedReports[phoneNumber].primary_status] || 0;
        const newPriority = statusPriority[report.status] || 0;
        
        if (newPriority > currentPriority) {
          groupedReports[phoneNumber].primary_status = report.status;
        }
        
        // Use city from latest report if available
        if (report.city) {
          groupedReports[phoneNumber].city = report.city;
        }
      });
      
      // Convert to array and sort by latest date
      const groupedArray = Object.values(groupedReports).sort((a, b) => 
        new Date(b.latest_date) - new Date(a.latest_date)
      );
      
      setReports(groupedArray);
    } catch (error) {
      console.error("Fetch reports error:", error);
      toast.error("Error fetching reports");
    }
  };

  const fetchAnalytics = async () => {
    try {
      console.log("🔍 Fetching analytics from:", `${API}/analytics`);
      console.log("🌐 Backend URL:", BACKEND_URL);
      console.log("📊 Full API URL:", `${API}/analytics`);
      const response = await axios.get(`${API}/analytics`);
      setAnalytics(response.data);
      console.log("📈 Analytics data:", response.data);
    } catch (error) {
      console.error("Fetch analytics error:", error);
      toast.error("Error fetching analytics");
    }
  };


  const submitReport = async () => {
    // Validate all required fields
    if (!newReport.phone_number.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    
    if (!newReport.status) {
      toast.error("Please select a status");
      return;
    }
    
    if (!newReport.city) {
      toast.error("Please select a city");
      return;
    }
    
    if (!newReport.comment.trim()) {
      toast.error("Please add a comment");
      return;
    }

    try {
      // Add captcha_response for backend compatibility
      const reportData = {
        ...newReport,
        captcha_response: "verified" // Auto-verified for private site
      };
      
      await axios.post(`${API}/reports`, reportData);
      toast.success("Report submitted successfully");
      setShowAddForm(false);
      setNewReport({
        phone_number: "",
        status: "",
        city: "",
        comment: "",
        username: ""
      });
      
      // Refresh analytics data
      fetchAnalytics();
    } catch (error) {
      console.error("Submit report error:", error);
      toast.error("Error submitting report");
    }
  };

  // Admin functions
  const fetchAllReports = async () => {
    try {
      console.log("🔍 Fetching admin reports from:", `${API}/reports?limit=2000`);
      const response = await axios.get(`${API}/reports?limit=2000`);
      setAdminReports(response.data);
      console.log(`📊 Admin loaded ${response.data.length} reports`);
      toast.success(`Loaded ${response.data.length} reports`);
    } catch (error) {
      console.error("Error fetching all reports:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Error fetching reports: ${errorMessage}`);
    }
  };

  const fetchAdminStats = async () => {
    try {
      console.log("📊 Fetching admin stats...");
      const response = await axios.get(`${API}/admin/stats`);
      setAdminStats(response.data);
      console.log("✅ Admin stats loaded:", response.data);
    } catch (error) {
      console.error("Error fetching admin stats:", error);
      toast.error("Error fetching statistics");
      // Set default stats if fetch fails
      setAdminStats({
        search_stats: { today: 0, week: 0, month: 0, total: 0 },
        report_stats: { today: 0, week: 0, month: 0, total: 0 },
        visit_stats: { today: 0, week: 0, month: 0 },
        recent_searches: []
      });
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce report ?")) return;
    
    try {
      await axios.delete(`${API}/reports/${reportId}`);
      toast.success("Report supprimé avec succès");
      fetchAllReports();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const updateReport = async (reportId, updatedData) => {
    try {
      await axios.put(`${API}/reports/${reportId}`, updatedData);
      toast.success("Report mis à jour avec succès");
      setEditingReport(null);
      fetchAllReports();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const openAdminPanel = () => {
    if (!isAuthenticated) {
      toast.error("Vous devez être connecté pour accéder à l'admin");
      return;
    }
    
    const adminPassword = prompt("Mot de passe admin:");
    if (adminPassword !== "ADMIN123") {
      toast.error("Mot de passe admin incorrect");
      return;
    }
    
    setShowAdminPanel(true);
    fetchAllReports();
  };

  const filterAndShowReports = async (filterType) => {
    try {
      setCurrentFilter(filterType);
      setShowFilteredResults(true);
      
      // Fetch filtered reports
      const url = filterType === "all" 
        ? `${API}/reports?limit=2000` 
        : `${API}/reports?filter_by=${filterType}&limit=2000`;
      
      console.log(`🔍 Filtering reports by: ${filterType}, URL: ${url}`);
      const response = await axios.get(url);
      
      // Group reports by phone number like in fetchReports
      const groupedReports = {};
      response.data.forEach(report => {
        const phoneNumber = report.phone_number;
        if (!groupedReports[phoneNumber]) {
          groupedReports[phoneNumber] = {
            phone_number: phoneNumber,
            reports: [],
            all_comments: [],
            latest_date: report.created_at,
            primary_status: report.status,
            city: report.city
          };
        }
        
        groupedReports[phoneNumber].reports.push(report);
        if (report.comment) {
          groupedReports[phoneNumber].all_comments.push(report.comment);
        }
      });
      
      const groupedArray = Object.values(groupedReports).sort((a, b) => 
        new Date(b.latest_date) - new Date(a.latest_date)
      );
      
      setFilteredReports(groupedArray);
      toast.success(`Found ${groupedArray.length} numbers for ${filterType}`);
      
    } catch (error) {
      console.error("Filter error:", error);
      toast.error("Error filtering reports");
    }
  };

  // Existing functions...

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'dark bg-gray-900' : 'bg-white'}`}>
      <Toaster position="top-center" />
      
      {!isAuthenticated ? (
        /* Login Screen */
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img src="/bmg_logo.png" alt="BMG Logo" className="h-12 w-12 object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Check Client
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Enter password to access the database
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  className={authError ? 'border-red-500' : ''}
                />
                {authError && (
                  <p className="text-red-500 text-sm mt-2">{authError}</p>
                )}
              </div>
              <Button 
                onClick={handleLogin}
                className="w-full bg-[#7acfff] hover:bg-[#62d7d1] text-black"
              >
                Access Database
              </Button>
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsDark(!isDark)}
                  className="rounded-full"
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Main Application - Mobile-First Design */
        <>
          {/* Header */}
          <div className={`min-h-screen ${isDark ? 'dark bg-gray-900' : 'bg-white'}`}>
            {/* Top Bar with Logo and Controls */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <img src="/bmg_logo.png" alt="BMG" className="h-5 w-5 object-contain" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsDark(!isDark)}
                  className="rounded-full"
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {getAuthTimeRemaining()}
                </span>
                <Button
                  variant="ghost" 
                  size="sm"
                  onClick={handleLogout}
                  className="text-red-600 hover:text-red-700"
                >
                  Logout
                </Button>
                
                <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#7acfff] hover:bg-[#62d7d1] text-black">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-col items-center">
              {activeTab === "search" ? (
                /* Search Page */
                <div className="flex flex-col items-center w-full max-w-md mx-auto px-4 pt-16 pb-8">
                  {/* Centered Title */}
                  <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Check Client</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Stay Secure, Stay Independent</p>
                  </div>

                  {/* Search Bar */}
                  <div className="relative w-full mb-6">
                    <div className="relative">
                      <Input
                        placeholder="Enter phone number (+4179....)"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        onFocus={() => searchQuery.length >= 3 && setShowLiveResults(true)}
                        className="w-full h-12 px-4 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-full shadow-sm focus:border-[#7acfff] focus:ring-2 focus:ring-[#7acfff]/20 dark:bg-gray-800 dark:text-white"
                      />
                      
                      {/* Live Search Results Dropdown */}
                      {showLiveResults && liveResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                          {liveResults.map((result, index) => (
                            <div
                              key={index}
                              onClick={() => selectLiveResult(result)}
                              className="px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900 dark:text-white text-base">
                                    {formatPhoneNumber(result.phone_number)}
                                  </p>
                                  <div className="space-y-1">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      {result.report_count} report{result.report_count > 1 ? 's' : ''}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      {getCountryAndProvider(result.phone_number)}
                                    </p>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  {(() => {
                                    const statusInfo = getStatusInfo(result.primary_status);
                                    const IconComponent = statusInfo.icon;
                                    return (
                                      <Badge className={`${statusInfo.className} text-xs`}>
                                        <IconComponent className="h-3 w-3 mr-1" />
                                        {statusInfo.label}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>
                              {result.sample_comment && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                                  {result.sample_comment}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Button */}
                  <div className="flex space-x-4 mb-8">
                    <Button
                      onClick={handleSearch}
                      disabled={loading}
                      className="bg-[#7acfff] hover:bg-[#62d7d1] text-black px-6"
                    >
                      {loading ? "Searching..." : "Search"}
                    </Button>
                  </div>

                  {/* Tab Navigation - Below Search */}
                  <div className="flex space-x-6 text-sm justify-center">
                    <button
                      onClick={() => {
                        setActiveTab("analytics");
                        fetchAnalytics(); // Refresh analytics data when clicking on tab
                        fetchAllReports(); // Also fetch admin reports for Last Reported Numbers
                      }}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 pb-1"
                    >
                      Analytics
                    </button>
                  </div>

                  {/* Search Results */}
                  {searchResult && (
                    <div className="w-full mt-8">
                      <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                        <CardContent className="pt-6">
                          {searchResult.found ? (
                            searchResult.multiple_results ? (
                              /* Multiple Results Display */
                              <div className="space-y-4">
                                <div className="text-center mb-4">
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    Found {searchResult.total_matches} matching number{searchResult.total_matches > 1 ? 's' : ''}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Search: "{searchResult.search_query}"
                                  </p>
                                </div>
                                
                                <div className="space-y-3">
                                  {searchResult.results.map((result, index) => (
                                    <Card key={index} className="bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors cursor-pointer"
                                          onClick={() => handleSearchForNumber(result.phone_number)}>
                                      <CardContent className="pt-4">
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <h4 className="font-semibold text-gray-900 dark:text-white">
                                              {formatPhoneNumber(result.phone_number)}
                                            </h4>
                                            <div className="space-y-1">
                                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {result.report_count} report{result.report_count > 1 ? 's' : ''}
                                              </p>
                                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {getCountryAndProvider(result.phone_number)}
                                              </p>
                                            </div>
                                            
                                            {/* Multiple status badges */}
                                            {result.all_statuses && result.all_statuses.length > 1 ? (
                                              <div className="flex flex-wrap gap-1 mt-2">
                                                {result.all_statuses.map((status, idx) => {
                                                  const statusInfo = getStatusInfo(status);
                                                  const IconComponent = statusInfo.icon;
                                                  return (
                                                    <Badge key={idx} className={`${statusInfo.className} text-xs`}>
                                                      <IconComponent className="h-2 w-2 mr-1" />
                                                      {formatCityAndStatus(result.primary_city, status)}
                                                    </Badge>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              (() => {
                                                const statusInfo = getStatusInfo(result.primary_status);
                                                const IconComponent = statusInfo.icon;
                                                return (
                                                  <Badge className={`${statusInfo.className} text-xs mt-2`}>
                                                    <IconComponent className="h-2 w-2 mr-1" />
                                                    {formatCityAndStatus(result.primary_city, result.primary_status)}
                                                  </Badge>
                                                );
                                              })()
                                            )}
                                          </div>
                                          
                                          <div className="text-right">
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                              Last: {formatDate(result.last_report_date)}
                                            </p>
                                          </div>
                                        </div>
                                        
                                        {result.comments && result.comments.length > 0 && (
                                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 truncate">
                                            Latest: {result.comments[result.comments.length - 1]}
                                          </p>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              /* Single Result Display */
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                                      {formatPhoneNumber(searchResult.phone_number)}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      {getCountryFromPhone(searchResult.phone_number)}
                                    </p>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-2">
                                    {searchResult.all_statuses && searchResult.all_statuses.length > 1 ? (
                                      searchResult.all_statuses.map((status, idx) => {
                                        const statusInfo = getStatusInfo(status);
                                        const IconComponent = statusInfo.icon;
                                        return (
                                          <Badge key={idx} className={statusInfo.className}>
                                            <IconComponent className="h-3 w-3 mr-1" />
                                            {formatCityAndStatus(searchResult.city || searchResult.all_cities?.[0], status)}
                                          </Badge>
                                        );
                                      })
                                    ) : (
                                      (() => {
                                        const statusInfo = getStatusInfo(searchResult.status || searchResult.primary_status);
                                        const IconComponent = statusInfo.icon;
                                        return (
                                          <Badge className={statusInfo.className}>
                                            <IconComponent className="h-3 w-3 mr-1" />
                                            {formatCityAndStatus(searchResult.city || searchResult.all_cities?.[0], searchResult.status || searchResult.primary_status)}
                                          </Badge>
                                        );
                                      })()
                                    )}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-600 dark:text-gray-400">Reports:</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {searchResult.report_count || 1} consolidé 
                                      {searchResult.reports && searchResult.reports[0]?.original_report_count > 1 && 
                                        ` (origine: ${searchResult.reports[0].original_report_count})`
                                      }
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-600 dark:text-gray-400">Last Report:</p>
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {formatDate(searchResult.last_report_date)}
                                    </p>
                                  </div>
                                </div>

                                {searchResult.comments && searchResult.comments.length > 0 && (
                                  <div>
                                    {/* Only show "All Comments" title if there are multiple comments */}
                                    {searchResult.comments.length > 1 && (
                                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                                        All Comments ({searchResult.comments.length}):
                                      </p>
                                    )}
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                      {searchResult.comments.map((comment, index) => (
                                        <p key={index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-800 dark:text-gray-200">
                                          {comment}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          ) : (
                            <div className="text-center py-8">
                              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Reports Found</h3>
                              <p className="text-gray-600 dark:text-gray-400">This phone number is not in our database.</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ) : (
                /* Analytics - Full Screen */
                <div className="w-full max-w-6xl mx-auto px-4 py-6">
                  {/* Navigation Header for Analytics */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => setActiveTab("search")}
                      className="flex items-center text-[#7acfff] hover:text-[#62d7d1]"
                    >
                      <Search className="h-5 w-5 mr-2" />
                      Back to Search
                    </button>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Analytics
                    </h1>
                  </div>

                  {analytics && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("all")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Users className="h-8 w-8 text-[#7acfff] dark:text-[#7acfff]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Reports</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.total_reports}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("safe")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Shield className="h-8 w-8 text-[#62d7d1]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Safe Numbers</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.safe_count}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("time_wasters")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Clock className="h-8 w-8 text-[#ffc14d]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Time Wasters</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.time_wasters_count}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("health_risk_clients")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Activity className="h-8 w-8 text-[#f9663d]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Health Risk</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.health_risk_count}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("drugs_abusers")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Pill className="h-8 w-8 text-[#f7546b]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Drugs Abusers</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.drugs_count}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("non_payment_clients")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <DollarSign className="h-8 w-8 text-[#ffbb8a]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Non-Payment</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.non_payment_count}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("aggressive_clients")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Scissors className="h-8 w-8 text-[#f7546b]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aggressive</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.aggressive_count || 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-white dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => filterAndShowReports("no_show_clients")}>
                          <CardContent className="pt-6">
                            <div className="flex items-center">
                              <Eye className="h-8 w-8 text-[#ffd8e9]" />
                              <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No-Show</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.no_show_count || 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Provider Statistics Button - REMOVED 
                      <div className="mt-6 flex justify-center">
                        <Button 
                          className="bg-[#7acfff] hover:bg-[#62d7d1] text-black"
                          onClick={() => fetchProviderStats()}
                        >
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Provider Statistics
                        </Button>
                      </div>
                      */}

                      {/* Filtered Results */}
                      {showFilteredResults && (
                        /* Show Filtered Results instead of Last Reported Numbers */
                        <Card className="bg-white dark:bg-gray-800">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-gray-900 dark:text-white">
                                Filtered Results: {currentFilter.replace('_', ' ').toUpperCase()} ({filteredReports.length})
                              </CardTitle>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setShowFilteredResults(false)}
                              >
                                Close
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {filteredReports.map((report, index) => (
                                <Card key={index} className="bg-gray-50 dark:bg-gray-700 p-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900 dark:text-white">
                                        {formatPhoneNumber(report.phone_number)}
                                      </p>
                                      <div className="space-y-1 mt-2">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                          {report.reports.length} report{report.reports.length > 1 ? 's' : ''}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                          {getCountryAndProvider(report.phone_number)}
                                        </p>
                                      </div>
                                      <div className="mt-2">
                                        {report.all_comments.map((comment, idx) => (
                                          <p key={idx} className="text-xs text-gray-500 italic">
                                            "{comment}"
                                          </p>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-end space-y-2">
                                      {(() => {
                                        const statusInfo = getStatusInfo(report.primary_status);
                                        const IconComponent = statusInfo.icon;
                                        return (
                                          <Badge className={statusInfo.className}>
                                            <IconComponent className="h-3 w-3 mr-1" />
                                            {statusInfo.label}
                                          </Badge>
                                        );
                                      })()}
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {new Date(report.latest_date).toLocaleDateString('fr-FR', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Add Report Dialog */}
          <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
            <DialogContent className="bg-white dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="text-gray-900 dark:text-white">Add New Report</DialogTitle>
                <DialogDescription className="text-gray-600 dark:text-gray-400">
                  Report a phone number as safe or blacklisted
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number *</label>
                  <Input
                    placeholder="Enter phone number (+4179...)"
                    value={newReport.phone_number}
                    onChange={(e) => setNewReport({...newReport, phone_number: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status *</label>
                  <Select value={newReport.status} onValueChange={(value) => setNewReport({...newReport, status: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="safe">🟢 Safe</SelectItem>
                      <SelectItem value="time_wasters">⏰ Time Wasters</SelectItem>
                      <SelectItem value="non_payment_clients">💰 Non-Payment Clients</SelectItem>
                      <SelectItem value="health_risk_clients">⚠️ Health Risk Clients</SelectItem>
                      <SelectItem value="drugs_abusers">💊 Drugs Abusers</SelectItem>
                      <SelectItem value="aggressive_clients">😡 Aggressive Clients</SelectItem>
                      <SelectItem value="no_show_clients">👻 No-Show Clients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">City *</label>
                  <Select value={newReport.city} onValueChange={(value) => setNewReport({...newReport, city: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Geneva">Genève</SelectItem>
                      <SelectItem value="Zurich">Zurich</SelectItem>
                      <SelectItem value="Lausanne">Lausanne</SelectItem>
                      <SelectItem value="Other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Comment *</label>
                  <Textarea
                    placeholder="Add detailed information about this client..."
                    value={newReport.comment}
                    onChange={(e) => setNewReport({...newReport, comment: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Username (Optional)</label>
                  <Input
                    placeholder="Your username (e.g., @sarah_geneva)"
                    value={newReport.username}
                    onChange={(e) => setNewReport({...newReport, username: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional: Add your username to identify your reports
                  </p>
                </div>
                
                <Button onClick={submitReport} className="w-full bg-[#7acfff] hover:bg-[#62d7d1] text-black">
                  Submit Report
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Phone Details Popup */}
          <Dialog open={showPhonePopup} onOpenChange={setShowPhonePopup}>
            <DialogContent className="bg-white dark:bg-gray-800 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900 dark:text-white">Phone Number Details</DialogTitle>
              </DialogHeader>
              
              {selectedPhoneData && (
                <div className="space-y-4">
                  {/* Phone Number Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-600">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {formatPhoneNumber(selectedPhoneData.phone_number)}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {getCountryFromPhone(selectedPhoneData.phone_number)}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {selectedPhoneData.all_statuses && selectedPhoneData.all_statuses.length > 1 ? (
                        selectedPhoneData.all_statuses.map((status, idx) => {
                          const statusInfo = getStatusInfo(status);
                          const IconComponent = statusInfo.icon;
                          return (
                            <Badge key={idx} className={statusInfo.className}>
                              <IconComponent className="h-3 w-3 mr-1" />
                              {formatCityAndStatus(selectedPhoneData.city || selectedPhoneData.all_cities?.[0], status)}
                            </Badge>
                          );
                        })
                      ) : (
                        (() => {
                          const statusInfo = getStatusInfo(selectedPhoneData.status || selectedPhoneData.primary_status);
                          const IconComponent = statusInfo.icon;
                          return (
                            <Badge className={statusInfo.className}>
                              <IconComponent className="h-3 w-3 mr-1" />
                              {formatCityAndStatus(selectedPhoneData.city || selectedPhoneData.all_cities?.[0], selectedPhoneData.status || selectedPhoneData.primary_status)}
                            </Badge>
                          );
                        })()
                      )}
                    </div>
                  </div>
                  
                  {/* Statistics */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Reports:</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedPhoneData.report_count}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Last Report:</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatDate(selectedPhoneData.last_report_date)}
                      </p>
                    </div>
                  </div>

                  {/* Comments */}
                  {selectedPhoneData.comments && selectedPhoneData.comments.length > 0 && (
                    <div>
                      {selectedPhoneData.comments.length > 1 && (
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                          All Comments ({selectedPhoneData.comments.length}):
                        </p>
                      )}
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {selectedPhoneData.comments.map((comment, index) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                            <p className="text-sm text-gray-800 dark:text-gray-200">
                              {comment}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          {/* Admin Panel Dialog */}
          <Dialog open={showAdminPanel} onOpenChange={setShowAdminPanel}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>🛡️ Panneau d'Administration</DialogTitle>
                <DialogDescription>
                  Gérer tous les reports - Supprimer, Modifier, Ajouter
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Statistics Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">📊 Statistiques de Recherche</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Recherches Aujourd'hui:</p>
                      <p className="font-bold text-lg">{adminStats?.search_stats?.today || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Cette Semaine:</p>
                      <p className="font-bold text-lg text-blue-600">
                        {adminStats?.search_stats?.week || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Ce Mois:</p>
                      <p className="font-bold text-lg text-green-600">
                        {adminStats?.search_stats?.month || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Recherches:</p>
                      <p className="font-bold text-lg text-purple-600">
                        {adminStats?.search_stats?.total || 0}
                      </p>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold mb-2 mt-4">📋 Statistiques Reports</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Reports Aujourd'hui:</p>
                      <p className="font-bold text-lg">{adminStats?.report_stats?.today || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Cette Semaine:</p>
                      <p className="font-bold text-lg text-blue-600">
                        {adminStats?.report_stats?.week || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Ce Mois:</p>
                      <p className="font-bold text-lg text-green-600">
                        {adminStats?.report_stats?.month || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Reports:</p>
                      <p className="font-bold text-lg text-purple-600">
                        {adminStats?.report_stats?.total || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Visit Statistics */}
                {adminStats?.visit_stats && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">👁️ Statistiques de Visites</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Visites Aujourd'hui:</p>
                        <p className="font-bold text-xl text-blue-600">{adminStats.visit_stats.today || 0}</p>
                        <p className="text-xs text-gray-500">Depuis 00h00</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Cette Semaine:</p>
                        <p className="font-bold text-xl text-green-600">{adminStats.visit_stats.week || 0}</p>
                        <p className="text-xs text-gray-500">7 derniers jours</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Ce Mois:</p>
                        <p className="font-bold text-xl text-purple-600">{adminStats.visit_stats.month || 0}</p>
                        <p className="text-xs text-gray-500">30 derniers jours</p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Moyenne Quotidienne (7j):</p>
                        <p className="font-bold text-lg text-indigo-600">
                          {Math.round((adminStats.visit_stats.week || 0) / 7)}
                        </p>
                        <p className="text-xs text-gray-500">visites/jour</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Moyenne Quotidienne (30j):</p>
                        <p className="font-bold text-lg text-pink-600">
                          {Math.round((adminStats.visit_stats.month || 0) / 30)}
                        </p>
                        <p className="text-xs text-gray-500">visites/jour</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Searches */}
                {adminStats?.recent_searches && adminStats.recent_searches.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">🔍 20 Dernières Recherches</h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {adminStats.recent_searches.map((search, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex-1">
                            <p className="font-medium text-[#7acfff]">
                              {formatPhoneNumber(search.phone_number)}
                            </p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{formatDate(search.searched_at)}</span>
                              {search.city && (
                                <>
                                  <span>•</span>
                                  <span>{formatCity(search.city)}</span>
                                </>
                              )}
                              <span>•</span>
                              <span>{getCountryAndProvider(search.phone_number)}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {search.has_reports ? (
                              (() => {
                                const statusInfo = getStatusInfo(search.status);
                                return (
                                  <Badge className={statusInfo.className + " text-xs"}>
                                    {statusInfo.label}
                                  </Badge>
                                );
                              })()
                            ) : (
                              <Badge className="bg-gray-200 text-gray-600 text-xs">
                                No reports
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Search and Stats */}
                <div className="flex items-center justify-between">
                  <Input
                    placeholder="Rechercher un numéro..."
                    value={adminSearchTerm}
                    onChange={(e) => setAdminSearchTerm(e.target.value)}
                    className="w-64"
                  />
                  <div className="text-sm text-gray-600">
                    Total: {adminReports.length} reports
                  </div>
                </div>
                
                {/* Reports Table */}
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Numéro</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-left">Ville</th>
                        <th className="px-3 py-2 text-left">Commentaire</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminReports && adminReports.length > 0 ? (
                        adminReports
                          .filter(report => 
                            !adminSearchTerm || 
                            report.phone_number?.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
                            report.comment?.toLowerCase().includes(adminSearchTerm.toLowerCase())
                          )
                          .map((report, index) => (
                            <tr key={report.id || index} className="border-t hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs">
                              {formatPhoneNumber(report.phone_number)}
                            </td>
                            <td className="px-3 py-2">
                              {(() => {
                                const statusInfo = getStatusInfo(report.status);
                                return (
                                  <Badge className={`${statusInfo.className} text-xs`}>
                                    {statusInfo.label}
                                  </Badge>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2 text-xs">{report.city}</td>
                            <td className="px-3 py-2 max-w-xs">
                              <div className="truncate text-xs" title={report.comment}>
                                {report.comment?.length > 50 ? 
                                  `${report.comment.substring(0, 50)}...` : 
                                  report.comment
                                }
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {new Date(report.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex space-x-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setEditingReport(report)}
                                >
                                  ✏️
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                  onClick={() => deleteReport(report.id)}
                                >
                                  🗑️
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                            {adminReports ? (
                              `Aucun report trouvé (${adminReports.length} total)`
                            ) : (
                              "Chargement des reports..."
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Add New Report Button */}
                <div className="flex justify-center">
                  <Button 
                    onClick={() => setEditingReport({})}
                    className="bg-[#7acfff] hover:bg-[#62d7d1] text-black"
                  >
                    ➕ Ajouter un nouveau report
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Edit Report Dialog */}
          <Dialog open={!!editingReport} onOpenChange={() => setEditingReport(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingReport?.id ? "✏️ Modifier Report" : "➕ Nouveau Report"}
                </DialogTitle>
              </DialogHeader>
              
              {editingReport && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Numéro de téléphone</label>
                    <Input
                      value={editingReport.phone_number || ""}
                      onChange={(e) => setEditingReport({
                        ...editingReport, 
                        phone_number: e.target.value
                      })}
                      placeholder="+41792123456"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Statut</label>
                    <Select 
                      value={editingReport.status || ""} 
                      onValueChange={(value) => setEditingReport({
                        ...editingReport, 
                        status: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="safe">🟢 Safe</SelectItem>
                        <SelectItem value="time_wasters">⏰ Time Wasters</SelectItem>
                        <SelectItem value="non_payment_clients">💰 Non-Payment</SelectItem>
                        <SelectItem value="health_risk_clients">⚠️ Health Risk</SelectItem>
                        <SelectItem value="drugs_abusers">💊 Drugs Abusers</SelectItem>
                        <SelectItem value="aggressive_clients">😡 Aggressive</SelectItem>
                        <SelectItem value="no_show_clients">👻 No-Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Ville</label>
                    <Select 
                      value={editingReport.city || ""} 
                      onValueChange={(value) => setEditingReport({
                        ...editingReport, 
                        city: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une ville" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Geneva">Genève</SelectItem>
                        <SelectItem value="Zurich">Zurich</SelectItem>
                        <SelectItem value="Lausanne">Lausanne</SelectItem>
                        <SelectItem value="Other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Commentaire</label>
                    <textarea
                      className="w-full p-3 border rounded-md resize-none min-h-[100px]"
                      value={editingReport.comment || ""}
                      onChange={(e) => setEditingReport({
                        ...editingReport, 
                        comment: e.target.value
                      })}
                      placeholder="Décrivez le problème ou la situation..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setEditingReport(null)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="bg-[#7acfff] hover:bg-[#62d7d1] text-black"
                      onClick={() => {
                        if (editingReport.id) {
                          // Modifier
                          updateReport(editingReport.id, editingReport);
                        } else {
                          // Créer nouveau
                          submitReport({ 
                            phone_number: editingReport.phone_number,
                            status: editingReport.status,
                            city: editingReport.city,
                            comment: editingReport.comment
                          });
                          setEditingReport(null);
                          fetchAllReports(); // Refresh admin list
                        }
                      }}
                    >
                      {editingReport.id ? "Mettre à jour" : "Créer"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          {/* Admin Button - Bottom Right (Desktop Only) */}
          <div className="hidden md:block fixed bottom-6 right-6 z-50">
            <Button
              size="lg"
              className="rounded-full w-16 h-16 bg-[#7acfff] hover:bg-[#62d7d1] text-black shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => {
                openAdminPanel();
              }}
            >
              <Shield className="h-6 w-6" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default App;